/** @typedef {import("../../../api/types").DeviceType} DeviceType */
import React from "react";
import {Button, FormGroup, Input, Label, ListGroup, ListGroupItem} from "reactstrap";
import SearchSelect from "../../../shared/components/SearchSelect";
import {parseFieldsList} from "../../../shared/utils/format";
import FormModal from "../../../shared/components/FormModal";
import {DeviceSummary} from "./OrderSummaries";
import RowActions from "../../../shared/components/RowActions";
import {useRowEditor} from "../../../hooks/useRowEditor";
import IconLabel from "../../../shared/components/IconLabel";
import {PlusIcon} from "../../../shared/components/icons";

/**
 * @typedef {{
 *   key: number,
 *   deviceTypeId: string,
 *   description: string,
 *   fields: Record<string, string>,
 * }} DeviceRow
 * @param {{
 *   rows: DeviceRow[],
 *   deviceTypes: DeviceType[],
 *   errors: Record<number, Record<string, string>>,
 *   onChange: (rows: DeviceRow[]) => void,
 * }} props
 */
const OrderDevicesEditor = ({rows, deviceTypes, errors, onChange}) => {
    const editor = useRowEditor(rows, onChange);
    const typeName = (id) => deviceTypes.find((dt) => String(dt.id) === id)?.name ?? "Sem tipo";

    return (
        <>
            {rows.length === 0 && <p className="text-muted">Nenhum equipamento adicionado.</p>}
            {rows.length > 0 && (
                <ListGroup className="mb-3">
                    {rows.map((row, index) => {
                        return (
                            <ListGroupItem className="d-flex align-items-start gap-2" key={row.key}>
                                <DeviceSummary
                                    typeName={typeName(row.deviceTypeId)}
                                    description={row.description}
                                    fields={row.fields}
                                    errors={Object.values(errors[index] ?? {})}
                                />
                                <RowActions
                                    noun="equipamento"
                                    onEdit={() => editor.edit(row.key)}
                                    onRemove={() => editor.remove(row.key)}
                                />
                            </ListGroupItem>
                        );
                    })}
                </ListGroup>
            )}
            <Button color="primary" outline size="sm" onClick={editor.add}>
                <IconLabel icon={<PlusIcon />}>Adicionar equipamento</IconLabel>
            </Button>

            <FormModal
                isOpen={editor.isOpen}
                toggle={editor.close}
                title={editor.current ? "Editar equipamento" : "Adicionar equipamento"}
                initial={{
                    deviceTypeId: editor.current?.deviceTypeId ?? "",
                    description: editor.current?.description ?? "",
                    fields: editor.current?.fields ?? {},
                }}
                canSave={(v) => Boolean(v.description.trim())}
                onSave={(v) => editor.save({...v, description: v.description.trim()})}
            >
                {(values, set) => {
                    const type = deviceTypes.find((dt) => String(dt.id) === values.deviceTypeId);
                    return (
                        <>
                            <FormGroup>
                                <Label for="device-type">Tipo de equipamento</Label>
                                <SearchSelect
                                    id="device-type"
                                    ariaLabel="Tipo de equipamento"
                                    placeholder="Buscar tipo…"
                                    emptyMessage="Nenhum tipo encontrado."
                                    value={values.deviceTypeId}
                                    // Fields belong to the type: reset them on change.
                                    onChange={(deviceTypeId) => set({deviceTypeId, fields: {}})}
                                    options={[
                                        {value: "", label: "Sem tipo"},
                                        ...deviceTypes.map((dt) => ({
                                            value: String(dt.id),
                                            label: dt.name,
                                            search: dt.description ?? "",
                                        })),
                                    ]}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="device-description">Descrição</Label>
                                <Input
                                    id="device-description"
                                    type="textarea"
                                    rows={3}
                                    autoFocus
                                    value={values.description}
                                    onChange={(e) => set({description: e.target.value})}
                                />
                            </FormGroup>
                            {parseFieldsList(type?.fields_list).map((name) => (
                                <FormGroup key={name}>
                                    <Label for={`device-field-${name}`}>{name}</Label>
                                    <Input
                                        id={`device-field-${name}`}
                                        placeholder="opcional"
                                        value={values.fields[name] ?? ""}
                                        onChange={(e) => set({fields: {...values.fields, [name]: e.target.value}})}
                                    />
                                </FormGroup>
                            ))}
                        </>
                    );
                }}
            </FormModal>
        </>
    );
};

export default OrderDevicesEditor;
